import { ref, computed, watch } from '@nuxtjs/composition-api';
import useCurrentCompany from '@/hooks/useCurrentCompany';
import { config, Vis } from './planConfig';
import type { PlanConfig, Feature } from './planConfig';
import useCurrentUser from '@/hooks/useCurrentUser';
import { resolvePlanTier } from './planTier';
import {
  hasLiveStripeCorePlan,
  resolveCorePlan,
  resolveMigratedCorePlan,
} from '@/hooks/useCompanyBillingInfo/planKey';

import {
  useGetCorePlanAccessQuery,
  useGetCompanySubscriptionsQuery,
  ProductKey,
  SubscriptionStatus,
  Subscription,
} from '@/gql/generated';

import { useApolloQuery } from '@/gql/apolloWrapper';
import CachePolicy from '@/gql/CachePolicy';
import getStripeCustomerId from '@/helpers/billing/getStripeCustomerId';

// ------------------------ //
//  Begin Functions
// ------------------------ //

interface PlanAccess {
  display: Vis;
  upsell?: string;
  showInNav: boolean;
  hasAccess: boolean;
  hideInNav: boolean;
  isUpsell: boolean;
  isUpcharge: boolean;
}

const mapPlanAccess = (
  config: PlanConfig<Feature>,
  isTierZero: boolean
): PlanConfig<PlanAccess> => {
  if (!config) {
    return {} as PlanConfig<PlanAccess>;
  }

  return Object.entries(config).reduce((acc, [key, value]) => {
    acc[key] = {
      ...value,
      showInNav:
        (value.display === Vis.SHOW ||
          value.display === Vis.UPSELL ||
          value.display === Vis.UPCHARGE) &&
        (!value.hideForT0 || !isTierZero),
      hasAccess: value.display === Vis.SHOW,
      hideInNav: value.display === Vis.HIDE,
      isUpsell: value.display === Vis.UPSELL,
      isUpcharge: value.display === Vis.UPCHARGE,
    };

    return acc;
  }, {} as PlanConfig<PlanAccess>);
};

// ------------------------ //
//  End Functions
// ------------------------ //

let init;

const isReady = ref(false);
const { currentUser } = useCurrentUser();

const allActiveSubscriptions = ref<Subscription[]>([]);
const isLoading = ref(false);
const currentPlan = ref<ProductKey>();

const checkForPackage = (products, productType: ProductKey) =>
  products.some(
    (s) =>
      s.productCode === productType &&
      (s.status === SubscriptionStatus.Active ||
        s.status === SubscriptionStatus.Trialing)
  );

const getTrialingStatus = (products: Subscription[] = []) =>
  products.some(
    (s) =>
      [ProductKey.Lite, ProductKey.Core, ProductKey.Plus].includes(
        s.productCode
      ) && s.status === SubscriptionStatus.Trialing
  );

// The plan-access query reads the same getBillingInfo field as
// GET_BILLING_INFO with different coreSubscription fields, and
// coreSubscription has no id, so it skips the cache rather than replace Plan
// Info's copy (BIO-2085).
const CORE_PLAN_ACCESS_OPTIONS = { fetchPolicy: CachePolicy.NO_CACHE };

let retryCount = 0;
const setupHook = () => {
  watch(
    currentUser,
    (user) => {
      const stripeCustomerId = getStripeCustomerId(user?._company);
      if (stripeCustomerId) {
        const { onResult, onError } = useApolloQuery(
          useGetCompanySubscriptionsQuery,
          {
            data: {
              stripeCustomerId,
            },
          }
        );

        onError((errors) => {
          if (retryCount < 3 && !!errors.networkError?.message) {
            retryCount += 1;

            setupHook();
          }
        });

        onResult(({ companySubscriptions: res }) => {
          allActiveSubscriptions.value = res.filter(
            (s) =>
              s.status === SubscriptionStatus.Active ||
              s.status === SubscriptionStatus.Trialing
          ) as Subscription[];

          if (checkForPackage(res, ProductKey.Lite)) {
            currentPlan.value = ProductKey.Lite;
          }

          if (checkForPackage(res, ProductKey.Plus)) {
            currentPlan.value = ProductKey.Plus;
          }

          if (checkForPackage(res, ProductKey.Pause)) {
            currentPlan.value = ProductKey.Pause;
          }

          if (checkForPackage(res, ProductKey.Storage)) {
            currentPlan.value = ProductKey.Storage;
          }

          if (checkForPackage(res, ProductKey.Elite)) {
            currentPlan.value = ProductKey.Elite;
          }

          // A company migrated to Recurly keeps its Stripe customer, but its
          // Stripe core subscription is cancelled (BIO-2085). Only when no
          // live Stripe core plan is listed, ask getBillingInfo, and use only
          // a RECURLY answer. A STRIPE answer, no answer or an error gets
          // today's Core fallback.
          const companyId = user?._company?._id;
          if (!currentPlan.value && companyId && !hasLiveStripeCorePlan(res)) {
            const { onResult: onBillingInfo, onError: onBillingInfoError } =
              useApolloQuery(
                useGetCorePlanAccessQuery,
                {
                  input: {
                    companyId,
                  },
                },
                {},
                {},
                CORE_PLAN_ACCESS_OPTIONS
              );

            onBillingInfoError(() => {
              currentPlan.value = ProductKey.Core;
              isReady.value = true;
            });

            onBillingInfo(({ getBillingInfo: info }) => {
              currentPlan.value =
                resolveMigratedCorePlan(info?.coreSubscription) ||
                ProductKey.Core;
              isReady.value = true;
            });

            return;
          }

          // Fallback plan for now.
          if (!currentPlan.value) {
            currentPlan.value = ProductKey.Core;
          }

          isReady.value = true;
        });
      } else if (user?._company?._id) {
        // No Stripe customer (a Recurly-only company, BIO-2085): there are no
        // Stripe subscriptions to list, so read the core plan from
        // billing-service (Core, Elite, or Pause when paused). No core
        // subscription leaves currentPlan undefined — the company is
        // genuinely planless — but the answer is still in.
        const { onResult, onError } = useApolloQuery(
          useGetCorePlanAccessQuery,
          {
            input: {
              companyId: user._company._id,
            },
          },
          {},
          {},
          CORE_PLAN_ACCESS_OPTIONS
        );

        onError((errors) => {
          if (retryCount < 3 && !!errors.networkError?.message) {
            retryCount += 1;

            setupHook();
          }
        });

        onResult(({ getBillingInfo: res }) => {
          currentPlan.value = resolveCorePlan(res?.coreSubscription);

          isReady.value = true;
        });
      }
    },
    { immediate: true }
  );
};

// This will eventually be delivered directly from the BE, for now we're pulling it from our local static config.
const currentPlanConfig = computed(() =>
  currentPlan.value ? config[currentPlan.value] : {}
);

// ------------------------ //
//  Plan helpers
// ------------------------ //
const isBambeeLite = computed(() => currentPlan.value === ProductKey.Lite);
const isBambeeBusiness = computed(() => currentPlan.value === ProductKey.Core);
const isBambeePlus = computed(() => currentPlan.value === ProductKey.Plus);
const isBambeePause = computed(() => currentPlan.value === ProductKey.Pause);
const isBambeeElite = computed(() => currentPlan.value === ProductKey.Elite);

const isBambeeStorage = computed(
  () => currentPlan.value === ProductKey.Storage
);

const isTrial = computed(() => getTrialingStatus(allActiveSubscriptions.value));

const hasBasicAccess = computed(() => {
  return !(isBambeePause.value || isBambeeStorage.value);
});

const hasLiteOrLower = computed(() => {
  return isBambeeLite.value || isBambeePause.value || isBambeeStorage.value;
});

// Tier comes from the anchored `_tN` suffix of the plan value (built by
// billing-service as `${priceId}_t${tierNumber}`). Never substring-match the
// value — the Stripe price id is random and can contain a decoy `t<digit>`
// (BIO-2000). A value with no tier suffix resolves to `undefined`, never 0,
// so not-yet-loaded does not collapse into tier-zero (`hideForT0`).
//
// Deliberately no catalog dependency: `useCurrentPlan` calls `useStore()`,
// which throws when first evaluated outside setup — e.g. during render via
// TheNav's Options-API computeds — and its catalog snapshot is not reactive
// to the price-plans fetch resolving (PR #4085 review). The anchored suffix
// fixes the stated defect with no ordering risk.
const { company } = useCurrentCompany();

const tier = computed(() => resolvePlanTier(undefined, company.value.plan));

const isTierZero = computed(() => tier.value === 0);
const isTierOne = computed(() => tier.value === 1);
const isTierTwo = computed(() => tier.value === 2);
const isTierThree = computed(() => tier.value === 3);
const isTierFour = computed(() => tier.value === 4);
const isTierFive = computed(() => tier.value === 5);
const isTierSix = computed(() => tier.value === 6);

const planAccessMap = computed(() =>
  mapPlanAccess(currentPlanConfig.value, isTierZero.value)
);

const usePlanAccess = (force = false) => {
  if (!init || force) {
    setupHook();
    init = true;
  }

  return {
    isReady,
    isLoading,

    planAccessMap,
    currentPlan,

    isBambeeLite,
    isBambeeBusiness,
    isBambeePlus,
    isBambeePause,
    isBambeeStorage,
    isBambeeElite,
    isTrial,

    hasBasicAccess,
    hasLiteOrLower,

    allActiveSubscriptions,
    isTierZero,
    isTierOne,
    isTierTwo,
    isTierThree,
    isTierFour,
    isTierFive,
    isTierSix,
    tier,
  };
};

export default usePlanAccess;
